import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export interface Rating {
  id: number;
  job_id: number;
  rating_user_id: number;
  rated_user_id: number;
  rating: number;
  comment: string;
  category?: string;
  created_at: string;
  updated_at: string;
  rater_name?: string;
  rater_avatar?: string;
  rated_name?: string;
  job_title?: string;
}

export interface RatingStats {
  average_rating: number;
  total_ratings: number;
  category_averages?: Record<string, number>;
}

export interface RatingsState {
  ratings: Rating[];
  stats: RatingStats | null;
  loading: boolean;
  error: string | null;
}

const initialState: RatingsState = {
  ratings: [],
  stats: null,
  loading: false,
  error: null,
};

export const fetchUserRatings = createAsyncThunk(
  'ratings/fetchUserRatings',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/ratings/user/${userId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch ratings');
      }
      return rejectWithValue('Failed to fetch ratings');
    }
  }
);

export const fetchRatingCategories = createAsyncThunk(
  'ratings/fetchRatingCategories',
  async (userId: number, { rejectWithValue }) => {
    try {
      const response = await api.get(`/ratings/user/${userId}/categories`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch rating categories');
      }
      return rejectWithValue('Failed to fetch rating categories');
    }
  }
);

export const createRating = createAsyncThunk(
  'ratings/createRating',
  async (
    data: {
      jobId: number;
      ratedUserId: number;
      rating: number;
      qualityRating?: number;
      communicationRating?: number;
      timelinessRating?: number;
      professionalismRating?: number;
      comment?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post('/ratings', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to create rating');
      }
      return rejectWithValue('Failed to create rating');
    }
  }
);

const ratingsSlice = createSlice({
  name: 'ratings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserRatings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserRatings.fulfilled, (state, action) => {
        state.loading = false;
        state.ratings = action.payload.ratings || action.payload;
      })
      .addCase(fetchUserRatings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchRatingCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRatingCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
      })
      .addCase(fetchRatingCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createRating.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRating.fulfilled, (state, action) => {
        state.loading = false;
        state.ratings.unshift(action.payload);
      })
      .addCase(createRating.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default ratingsSlice.reducer;
